import { SignalRMessage } from '../context/NotificationContext';
import { Colors } from '../theme/colors';
import { toUtcIso, parseBackendDateOnly } from './dateUtils';

export const COLOR_MAP: Record<string, string> = {
  Attendance: Colors.success,
  Payment: Colors.primary,
  Canteen: Colors.warning,
  Homework: Colors.accent,
  Announcement: Colors.info,
  Library: Colors.info,
  Emergency: Colors.error,
  Default: Colors.info,
};

export type ActivityItem = {
  id: string;
  name: string;
  badge: { text: string; color: string };
  details: string;
  /**
   * Offset-qualified UTC. The API sends these without a timezone marker, which
   * JavaScript parses as LOCAL time - every screen rendering the value was showing
   * it shifted by the device's offset. Normalised here, at the one boundary the
   * data enters the app model, so no consumer has to remember to (see dateUtils).
   */
  createdAt: string;
  isNew?: boolean;
  /** Set for persisted feed items — the id the read/acknowledge endpoints take. */
  recipientId?: string;
  requiresAcknowledgement?: boolean;
  acknowledgedAt?: string | null;
  /** Comms category constant (EMERGENCY drives the banner + siren). */
  category?: string;
  /** NO_REPLY / REPLY_TO_TEACHER / REPLY_TO_DEPARTMENT / CREATE_TICKET. */
  replyMode?: string;
  replyTargetUserId?: string | null;
  /**
   * Receipts only. A delivery that did NOT arrive is the one thing a receipt says
   * that the feed row for the same message cannot, so it survives the de-duplication
   * in useParentFeed while a successful one is folded away.
   */
  deliveryFailed?: boolean;
};

/** Category (comms engine) → the badge vocabulary this app already renders. */
const CATEGORY_BADGES: Record<string, string> = {
  ATTENDANCE: 'Attendance',
  FINANCE: 'Payment',
  ACADEMIC: 'Homework',
  LIBRARY: 'Library',
  EMERGENCY: 'Emergency',
  GENERAL: 'Announcement',
  EVENT: 'Announcement',
  TRANSPORT: 'Announcement',
  // The receipt history labels engine messages COMMUNICATION where the feed calls
  // the same message ACADEMIC/FINANCE/etc. Unmapped, it fell through to the raw
  // category and put a meaningless "Communication" badge on the row.
  COMMUNICATION: 'Announcement',
};

/**
 * One email or push RECEIPT (GET /Notifications/my) as an ActivityItem.
 *
 * A receipt, not a message: the server returns a subject and an outcome, never a
 * body, because bodies carry fee amounts, absences and marks. So there is nothing
 * to open and no recipientId — which is also what stops the screen offering an
 * acknowledge button on a row that has nothing to acknowledge against.
 *
 * A failed delivery says so in the row. The parent seeing "did not arrive" against
 * something the school sent is the entire point of giving them this history.
 */
export function receiptToActivity(item: any): ActivityItem {
  const badgeText = CATEGORY_BADGES[(item.category ?? '').toUpperCase()]
    || item.category
    || 'Announcement';

  const channel = item.channel === 'EMAIL' ? 'Email' : 'Push';

  const outcome = item.status === 'DELIVERED' ? channel
    : item.status === 'PENDING' ? `${channel} · sending`
      : `${channel} · did not arrive`;

  return {
    // Prefixed because these ids come from different tables than the feed's and
    // could otherwise collide in the merged list.
    id: `receipt-${item.channel}-${item.sentAt}-${item.title}`,
    name: item.title || 'Notification',
    badge: { text: badgeText, color: COLOR_MAP[badgeText] || COLOR_MAP['Default'] },
    details: outcome,
    createdAt: toUtcIso(item.sentAt),
    // Never "new": a receipt is a record that something already went out, not an
    // unread message, and counting it would inflate the bell badge.
    isNew: false,
    category: (item.category ?? '').toUpperCase(),
    // PENDING is still in flight, not a failure — only a settled non-delivery counts.
    deliveryFailed: item.status !== 'DELIVERED' && item.status !== 'PENDING',
  };
}

/**
 * Groups a receipt with the feed row for the same message.
 *
 * Subject is the only field the two endpoints share — the feed returns
 * `subject`, the receipt history returns `title`, and they are the same string
 * for a given communication.
 */
export function notificationSubjectKey(subject?: string | null): string {
  return (subject ?? '').trim().toLowerCase();
}

/** One persisted feed entry (GET /Notifications/Parent) as an ActivityItem. */
export function feedItemToActivity(item: any): ActivityItem {
  const badgeText = CATEGORY_BADGES[item.category] || 'Announcement';
  const details = [item.studentName, stripHtml(item.body) || item.subject]
    .filter(Boolean)
    .join(' • ');

  return {
    id: `feed-${item.recipientId}`,
    recipientId: item.recipientId,
    name: item.subject,
    badge: { text: badgeText, color: COLOR_MAP[badgeText] || COLOR_MAP['Default'] },
    details,
    createdAt: toUtcIso(item.createdDate),
    isNew: item.readAt == null,
    requiresAcknowledgement: item.requiresAcknowledgement === true,
    acknowledgedAt: item.acknowledgedAt ?? null,
    category: item.category,
    replyMode: item.replyMode,
    replyTargetUserId: item.replyTargetUserId ?? null,
  };
}

function stripHtml(value?: string | null): string {
  if (!value) return '';
  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Maps the real payload shapes the backend sends today (SignalRMessageService.cs)
// into a readable title + message.
export function toActivity(msg: SignalRMessage, isNew: boolean): ActivityItem {
  const description = msg.eventDescription || '';
  const payload = (msg.payload || {}) as Record<string, any>;

  let name = 'Announcement';
  let badgeText = 'Notification';
  let details = description || 'No details available';

  if (msg.eventType === 'CanteenOrder') {
    const isNewOrder = payload.Type === 'OrderCreated';
    name = isNewOrder ? 'Canteen Order Placed' : 'Canteen Order Update';
    badgeText = 'Canteen';
    details = [
      payload.OrderNumber ? `Order #${payload.OrderNumber}` : null,
      payload.OrderStatus,
      payload.PaymentStatus ? `Payment: ${payload.PaymentStatus}` : null,
    ]
      .filter(Boolean)
      .join(' • ') || description;
  } else if (/attendance/i.test(description)) {
    name = 'Attendance Update';
    badgeText = 'Attendance';
    details = description;
  } else if (/homework/i.test(description)) {
    name = 'Homework Assigned';
    badgeText = 'Homework';
    details = [
      payload.SubjectName,
      payload.ClassName,
      // A due date names a calendar day, not an instant, so it must read as the
      // same day everywhere - parseBackendDateOnly pins it to local midnight
      // rather than letting a UTC-midnight parse show the previous day.
      payload.DueDate
        ? `Due ${parseBackendDateOnly(payload.DueDate)?.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          }) ?? payload.DueDate}`
        : null,
    ]
      .filter(Boolean)
      .join(' • ') || description;
  } else if (/fee payment/i.test(description)) {
    name = 'Fee Payment Received';
    badgeText = 'Payment';
    const components = Array.isArray(payload.Components)
      ? payload.Components.join(', ')
      : null;
    details = [
      payload.StudentName,
      payload.TotalPaidAmount != null ? `₹${payload.TotalPaidAmount} paid` : null,
      components,
    ]
      .filter(Boolean)
      .join(' • ') || description;
  } else {
    name = 'Announcement';
    badgeText = 'Announcement';
    details = description || 'No details available';
  }

  return {
    id: msg.messageId || `${msg.eventType}-${msg.createdAt}`,
    name,
    badge: {
      text: badgeText,
      color: COLOR_MAP[badgeText] || COLOR_MAP['Default'],
    },
    details,
    createdAt: toUtcIso(msg.createdAt),
    isNew,
  };
}
